import { GridBaseDataProvider } from '../../../../../../../../grid/GridBaseDataProvider';
import { Column, ColumnKeys } from './grid-config';
import {
  PolicyDependenciesContainer,
  UserGroupsApiClients,
} from '../../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import {
  GetRowsRequest,
  GetSummaryRequest,
  GetSummaryResponse,
  SetFiltersRequest,
  UpdateSettingsRequest,
} from '../../../../../../../../grid/types/common';
import { convertGridFiltersToExtJsFilters } from '../../../../../../../../grid/utils/filters';
import { UserGroupGridItem, UserGroupItem } from '@nirvana/shared/dtos/types/policies/common';
import { API_TYPE_POLICY_EXCHANGE } from '../../../../../../../../webcomponents.constants';

import sha1 from 'sha1';
import { Filter } from '../../../../../../../../grid/gridFilters/types';
import { USER_GROUPS_TYPE } from '../../../../../utils/uiSettings/exchange/exchange-protection.types';
import { UpdateSettingsResponse } from '@nirvana/shared/dtos/types/alerts/common';
import { PolicyPanelManager } from '../../../../../PolicyPanelManager';

export class GzUserGroupsDataProvider extends GridBaseDataProvider<Column, ColumnKeys> {
  protected items: UserGroupGridItem[];
  protected clients: UserGroupsApiClients;
  public companyFilterDataProvider = API_TYPE_POLICY_EXCHANGE;
  constructor() {
    super();
    this.clients = PolicyDependenciesContainer.getInstance().resolveDependency('UserGroups') as UserGroupsApiClients;
  }

  async getRows(request: GetRowsRequest<Column, ColumnKeys>): Promise<any> {
    if (!this.items) {
      console.error('No cached response');
    }
    return { rawEvents: this.items };
  }

  async getSummary(request: GetSummaryRequest<ColumnKeys>): Promise<GetSummaryResponse> {
    const sortColumnName = request.sortColumnName ?? null;
    const sortDirection = request.sortDirection ?? null;
    const params = {
      filter: convertGridFiltersToExtJsFilters(this.getConvertedFrontendFiltersToGzBackendFilters(request.filters)),
      limit: request.pageSize,
      page: request.pageNumber,
      sort: sortColumnName,
      dir: sortDirection,
    };

    try {
      const response = await this.clients.ReadRecords(params);
      this.items = this.getItemsWithAdditionalFrontendFields(response.data);
      return {
        summary: 'no Summary',
        numItems: response.total,
      };
    } catch (error) {
      this.items = [];
      return {
        summary: 'no Summary',
        numItems: 0,
      };
    }
  }

  async createRecord(records: UserGroupItem[]): Promise<any> {
    records.forEach(record => {
      record.specifics.guid = sha1(new Date() + Application.Main.user.userName);
    });
    return await this.clients.CreateRecords(records);
  }

  async updateRecords(records: UserGroupItem[]): Promise<any> {
    return await this.clients.UpdateRecords(records);
  }

  async removeRecords(records: UserGroupItem[]): Promise<any> {
    const customUserGroups = records.filter(record => record.type !== USER_GROUPS_TYPE.AD);
    const response = await this.clients.RemoveRecords(customUserGroups);
    PolicyPanelManager.getInstance().removeDeletedGroupsFromRecord(customUserGroups.map(group => group.specifics.guid));
    return response;
  }

  private getConvertedFrontendFiltersToGzBackendFilters(
    filters: Filter<ColumnKeys | 'specifics.company.id'>[]
  ): Filter<ColumnKeys | 'specifics.company.id'>[] {
    const companyFilterIdx = filters.findIndex(filter => Object.keys(filter)[0] === ColumnKeys.COMPANY);
    if (companyFilterIdx === -1) {
      return filters;
    }
    if (
      filters[companyFilterIdx][ColumnKeys.COMPANY].in.length === 1 &&
      filters[companyFilterIdx][ColumnKeys.COMPANY].in[0] === 'all'
    ) {
      delete filters[companyFilterIdx];
      return filters;
    }
    const companies = [...filters[companyFilterIdx][ColumnKeys.COMPANY].in];
    delete filters[companyFilterIdx];
    filters.push({ 'specifics.company.id': { in: companies } });
    return filters;
  }

  async setFilters(request: SetFiltersRequest<ColumnKeys>): Promise<{} | undefined> {
    return {};
  }

  protected getItemsWithAdditionalFrontendFields(items: UserGroupItem[]): UserGroupGridItem[] {
    return items.map(item => {
      return {
        ...item,
        emails: item.specifics.users !== undefined ? item.specifics.users.join('; ') : '',
      };
    });
  }

  shouldDataReloadOnResetView(): boolean {
    return true;
  }

  async updateSettings(params: UpdateSettingsRequest<Column>): Promise<UpdateSettingsResponse> {
    return {};
  }
}
